import React, { useState } from 'react';
import { get, ref } from 'firebase/database';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { db } from '../firebase';
import { Student } from '../types';

const degreeLabels: Record<string, string> = {
  bachelor: 'Bachelor',
  master: 'Master',
  doctorate: 'Doctorate',
};

const normalizeStudentId = (value: unknown) => String(value || '').trim().replace(/[^a-z0-9]/gi, '').toUpperCase();

const formatDate = (timestamp: number) => {
  if (!Number.isFinite(timestamp)) return '-';

  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(timestamp));
};

const StudentVerification: React.FC = () => {
  const [studentId, setStudentId] = useState('');
  const [student, setStudent] = useState<Student | null>(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const normalizedStudentId = normalizeStudentId(studentId);
    setStudent(null);
    setSearched(false);
    setError('');

    if (!normalizedStudentId) {
      setError('Please enter a student number.');
      return;
    }

    setLoading(true);
    try {
      const snapshot = await get(ref(db, 'students'));
      const students = snapshot.val() ? Object.values(snapshot.val()) as Student[] : [];
      const matchingStudent = students.find(item => normalizeStudentId(item.studentId) === normalizedStudentId) || null;

      setStudent(matchingStudent);
      setSearched(true);
    } catch (err) {
      setError('The verification service is temporarily unavailable. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-page verify-page">
      <Navbar />

      <section className="form-hero verify-hero">
        <span className="section-eyebrow">Official Student Verification</span>
        <h1>Student Record Verification</h1>
        <p>Verify a University of American Atlantic student record by student number.</p>
      </section>

      <main className="form-body verify-body">
        <form className="verify-search" onSubmit={handleSubmit}>
          <div className="form-group verify-input">
            <label htmlFor="studentId">Student Number</label>
            <input
              id="studentId"
              type="text"
              value={studentId}
              onChange={event => {
                setStudentId(event.target.value);
                setError('');
                setSearched(false);
              }}
              placeholder="Example: 2025010001"
              autoComplete="off"
            />
          </div>
          <button type="submit" className="btn-submit verify-submit" disabled={loading}>
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>

        {error && <div className="verify-alert verify-alert-error">{error}</div>}

        {searched && student && (
          <section className="verify-result verify-result-found" aria-live="polite">
            <div className="verify-status">Record Found</div>
            <div className="verify-student-name">{student.fullName}</div>
            <div className="verify-grid">
              <div>
                <span>Student Number</span>
                <strong>{student.studentId}</strong>
              </div>
              <div>
                <span>Program</span>
                <strong>{student.program}</strong>
              </div>
              <div>
                <span>Degree Level</span>
                <strong>{degreeLabels[student.degree] || student.degree}</strong>
              </div>
              <div>
                <span>Registration Date</span>
                <strong>{formatDate(student.createdAt)}</strong>
              </div>
            </div>
          </section>
        )}

        {searched && !student && (
          <section className="verify-result verify-result-empty" aria-live="polite">
            <div className="verify-status">No Record Found</div>
            <p>No student record was found for this student number.</p>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default StudentVerification;
